import React, { useCallback, useEffect, useState } from "react";
import api from "../../services/api";
import downloadFile from "../../services/downloadFile";
import { imprimerDocument } from "../../services/imprimer";
import { qlAlert, qlConfirm } from "../../utils/dialogs";
import DocumentViewer from "../DocumentViewer";
import { Chargement, Chip, Entete, Fenetre, Kpi, Ligne, Message } from "../compta/ComptaEspace";
import { useFiches } from "./CmContenus";
import {
  CANAUX, MULTIPART, SOURCES, STATUTS_DEMANDE, TON_CONTENU, TON_DEMANDE,
  dateHeure, erreurApi, formulaire, heureCameroun, jourCameroun, jourCourt, libelleMois, nombre, ouvrirPage, pourcent,
} from "./cmOutils";

const TONS = { navy: "#203e60", amber: "#d68600", red: "#e64049", green: "#15a652" };

// ── Tableau de bord ─────────────────────────────────────────────────────────

export function CMDashboardPage({ user }) {
  const [donnees, setDonnees] = useState(null);
  const [erreur, setErreur] = useState("");
  const charger = useCallback(() => {
    api.get("cm/dashboard/").then((r) => setDonnees(r.data)).catch((e) => setErreur(erreurApi(e, "Chargement impossible.")));
  }, []);
  useEffect(charger, [charger]);
  const { ouvrir, preparer, elements } = useFiches(charger);
  const ouvrirContenu = (id) => api.get(`cm/contenus/${id}/`).then((r) => ouvrir(r.data)).catch(() => {});

  if (erreur) return <div className="qc"><div className="qc-card qc-empty">{erreur}</div></div>;
  if (!donnees) return <div className="qc"><Chargement /></div>;
  const { semaine, demandes, campagne_active: campagne } = donnees;
  const prenom = (user?.nom_complet || "").split(" ")[0];
  return (
    <div className="qc">
      <Entete titre={`Bonjour${prenom ? ` ${prenom}` : ""}, voici tes priorités`}
        sousTitre="Préparer les contenus, animer la communauté et suivre les résultats.">
        <button className="qc-btn qc-btn--primary" onClick={preparer}>+ Préparer un contenu</button>
      </Entete>
      <div className="qc-kpis">
        <Kpi label="Contenus prévus cette semaine" value={semaine.prevus} ton={TONS.navy}
          sub={`${semaine.publies} publié${semaine.publies > 1 ? "s" : ""} · ${semaine.a_publier} à publier`} />
        <Kpi label="En attente de validation" value={donnees.en_attente_validation} ton={TONS.amber} sub="Décision d’un super admin" />
        <Kpi label="Demandes à traiter" value={demandes.nouvelles + demandes.en_cours} ton={TONS.red}
          sub={`${demandes.nouvelles} nouvelle${demandes.nouvelles > 1 ? "s" : ""} · ${demandes.en_cours} en cours`} />
        <Kpi label="Visites issues des campagnes" value={donnees.suivi_visites?.configure ? donnees.suivi_visites.visites : "—"} ton={TONS.green}
          sub={donnees.suivi_visites?.configure ? `Cette semaine · ${donnees.suivi_visites.inscriptions} inscription${donnees.suivi_visites.inscriptions > 1 ? "s" : ""}` : "Suivi non configuré"} />
      </div>
      <div className="qc-cols">
        <div className="qc-card">
          <h3>À faire aujourd’hui <Chip>{new Date(`${donnees.aujourdhui}T12:00:00Z`).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", timeZone: "UTC" })}</Chip></h3>
          {donnees.taches.length === 0 && <div className="qc-empty">Rien d’urgent aujourd’hui.</div>}
          {donnees.taches.map((t, i) => (
            <div key={i} className={`cm-task${t.urgent ? " cm-task--urgent" : ""}`}>
              <div className="cm-task-num">{String(i + 1).padStart(2, "0")}</div>
              <div><strong>{t.titre}</strong><small>{t.detail}</small></div>
              <button className="qc-btn qc-btn--sm" onClick={() => (t.contenu_id ? ouvrirContenu(t.contenu_id) : ouvrirPage("cm_communaute"))}>
                {t.type === "demandes" ? "Traiter" : "Ouvrir"}
              </button>
            </div>
          ))}
        </div>
        {campagne ? (
          <div className="cm-campaign">
            <Chip>CAMPAGNE DU MOMENT</Chip>
            <h3>{campagne.titre}</h3>
            {campagne.message_cle && <p>{campagne.message_cle}</p>}
            <div className="cm-campaign-data">
              <span>{jourCourt(campagne.date_debut)} – {jourCourt(campagne.date_fin)}</span>
              {campagne.objectif && <span>Objectif : {campagne.objectif}</span>}
              {campagne.public && <span>Public : {campagne.public}</span>}
              <span>{campagne.nb_publies}/{campagne.nb_contenus} contenus publiés</span>
            </div>
            <button className="qc-btn" onClick={() => ouvrirPage("cm_contenus")}>Voir les contenus</button>
          </div>
        ) : (
          <div className="qc-card">
            <h3>Campagne du moment</h3>
            <div className="qc-empty">Aucune campagne en cours. Créez-en une depuis l’atelier des contenus (bouton « Campagnes »).</div>
          </div>
        )}
      </div>
      <div className="qc-cols qc-cols--half">
        <div className="qc-card">
          <h3>Prochaines publications</h3>
          {donnees.prochaines.length === 0 && <div className="qc-empty">Aucune publication programmée.</div>}
          {donnees.prochaines.map((c) => (
            <Ligne key={c.id} onClick={() => ouvrir(c)}
              label={<>{jourCameroun(c.date_souhaitee) === donnees.aujourdhui ? "Aujourd’hui" : dateHeure(c.date_souhaitee).split(" à")[0]} · {heureCameroun(c.date_souhaitee)} — {c.titre}<br /><small>{c.format_label} · {c.canal_label}</small></>}
              valeur={<Chip ton={TON_CONTENU[c.statut]}>{c.statut_label}</Chip>} />
          ))}
          <button className="qc-btn qc-btn--sm" style={{ marginTop: 10 }} onClick={() => ouvrirPage("cm_calendrier")}>Voir le calendrier</button>
        </div>
        <div className="qc-card">
          <h3>Suivi de la communauté</h3>
          <Ligne label="Nouvelles demandes" valeur={demandes.nouvelles} onClick={() => ouvrirPage("cm_communaute")} />
          <Ligne label="Demandes en cours" valeur={demandes.en_cours} onClick={() => ouvrirPage("cm_communaute")} />
          <Ligne label="Demandes transmises au support" valeur={demandes.transmises} onClick={() => ouvrirPage("cm_communaute")} />
          <div className="qc-note">Les problèmes de paiement et de code de manuel sont confiés au support (super admins).</div>
        </div>
      </div>
      {elements}
    </div>
  );
}

// ── Communauté ──────────────────────────────────────────────────────────────

function FicheDemande({ id, reponses, onClose, onChange }) {
  const [demande, setDemande] = useState(null);
  const [texte, setTexte] = useState("");
  const [message, setMessage] = useState("");
  useEffect(() => { api.get(`cm/demandes/${id}/`).then((r) => setDemande(r.data)).catch(() => setMessage("Demande introuvable.")); }, [id]);
  const agir = async (action, confirmation) => {
    setMessage("");
    try {
      const r = await api.post(`cm/demandes/${id}/action/`, { action, texte });
      setDemande(r.data); setTexte(""); onChange();
      if (confirmation) qlAlert(confirmation, "success");
    } catch (err) { setMessage(erreurApi(err)); }
  };
  if (!demande) return <Fenetre titre="Demande" onClose={onClose}>{message ? <Message texte={message} /> : <Chargement />}</Fenetre>;
  const close = demande.statut === "cloturee";
  const transmissionReponse = demande.compte ? "dans sa messagerie Quick Learning"
    : /@/.test(demande.contact || "") ? "par e-mail" : `à la main (${demande.source_label})`;
  return (
    <Fenetre titre={`${demande.reference} · ${demande.sujet}`} onClose={onClose}>
      <div className="qc-detailgrid">
        <div>
          <Ligne label="Source" valeur={demande.source_label} />
          <Ligne label="Contact" valeur={<>{demande.contact_nom || "—"}{demande.contact ? <small> {demande.contact}</small> : null}</>} />
        </div>
        <div>
          <Ligne label="État" valeur={<Chip ton={TON_DEMANDE[demande.statut]}>{demande.statut_label}</Chip>} />
          <Ligne label="Prise en charge" valeur={demande.assignee || "—"} />
        </div>
      </div>
      {demande.message && <div className="cm-texte">{demande.message}</div>}
      <div className="qc-section-title">Échanges</div>
      <ul className="cm-echanges">
        {demande.echanges.map((e) => (
          <li key={e.id} className={`cm-echange--${e.type}`}>
            <small>{e.type_label} — {e.auteur}, {dateHeure(e.date)}{e.envoi ? ` · ${e.envoi}` : ""}</small>
            <span style={{ whiteSpace: "pre-wrap" }}>{e.contenu}</span>
          </li>
        ))}
        {!demande.echanges.length && <li><small>Aucun échange pour l’instant.</small></li>}
      </ul>
      {!close && (
        <>
          {reponses.length > 0 && (
            <select className="form-select form-select-sm mt-2" value="" onChange={(e) => setTexte(e.target.value)} aria-label="Réponse enregistrée">
              <option value="">Insérer une réponse enregistrée…</option>
              {reponses.map((r) => <option key={r.id} value={r.texte}>{r.titre}</option>)}
            </select>
          )}
          <textarea className="form-control mt-2" rows={4} value={texte} onChange={(e) => setTexte(e.target.value)}
            placeholder="Votre réponse ou votre note — relisez avant d’envoyer." />
          <div className="qc-foot">La réponse sera transmise {transmissionReponse}. Ne demandez jamais de mot de passe ni de code de manuel.</div>
        </>
      )}
      <Message texte={message} />
      <div className="qc-actions">
        {demande.statut === "nouvelle" && <button className="qc-btn" onClick={() => agir("prendre")}>Prendre en charge</button>}
        {!close && <>
          <button className="qc-btn qc-btn--primary" disabled={!texte.trim()} onClick={() => agir("repondre", "Réponse enregistrée.")}><i className="bi bi-reply"></i>Répondre</button>
          <button className="qc-btn" disabled={!texte.trim()} onClick={() => agir("note")}><i className="bi bi-journal"></i>Note interne</button>
          {demande.statut !== "transmise" && (
            <button className="qc-btn qc-btn--danger" onClick={() => agir("transmettre", "Demande transmise aux super admins.")}><i className="bi bi-headset"></i>Transmettre au support</button>
          )}
          <button className="qc-btn qc-btn--success" onClick={() => agir("cloturer")}><i className="bi bi-check2"></i>Clôturer</button>
        </>}
        {close && <button className="qc-btn" onClick={() => agir("rouvrir")}>Rouvrir</button>}
      </div>
    </Fenetre>
  );
}

function NouvelleDemande({ onClose, onSaved }) {
  const [valeurs, setValeurs] = useState({ sujet: "", message: "", source: "facebook", contact_nom: "", contact: "" });
  const [message, setMessage] = useState("");
  const maj = (cle) => (e) => setValeurs((v) => ({ ...v, [cle]: e.target.value }));
  const enregistrer = async () => {
    try { onSaved((await api.post("cm/demandes/", valeurs)).data); } catch (err) { setMessage(erreurApi(err)); }
  };
  return (
    <Fenetre titre="Saisir une demande reçue" onClose={onClose}>
      <div className="qc-form">
        <label>Source<select value={valeurs.source} onChange={maj("source")}>{SOURCES.filter(([k]) => k !== "formulaire").map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></label>
        <label>Sujet<input value={valeurs.sujet} onChange={maj("sujet")} maxLength={200} /></label>
        <label>Nom ou pseudo<input value={valeurs.contact_nom} onChange={maj("contact_nom")} maxLength={200} /></label>
        <label>Contact utile (facultatif)<input value={valeurs.contact} onChange={maj("contact")} maxLength={200} /></label>
        <label className="qc-full">Message reçu<textarea rows={4} value={valeurs.message} onChange={maj("message")} maxLength={3000} /></label>
      </div>
      <div className="qc-foot">Ne notez que les coordonnées nécessaires pour répondre.</div>
      <Message texte={message} />
      <div className="qc-actions"><button className="qc-btn qc-btn--primary" onClick={enregistrer}>Enregistrer</button></div>
    </Fenetre>
  );
}

function ReponsesEnregistrees({ reponses, onChange }) {
  const [voir, setVoir] = useState(null);
  const [ajout, setAjout] = useState(false);
  const [valeurs, setValeurs] = useState({ titre: "", texte: "" });
  const [message, setMessage] = useState("");
  const ajouter = async () => {
    try { await api.post("cm/reponses/", valeurs); setValeurs({ titre: "", texte: "" }); setAjout(false); onChange(); }
    catch (err) { setMessage(erreurApi(err)); }
  };
  const supprimer = (r) => qlConfirm(`Supprimer « ${r.titre} » ?`, async () => { await api.delete(`cm/reponses/${r.id}/`); onChange(); setVoir(null); },
    { danger: true, okLabel: "Supprimer" });
  return (
    <div className="qc-card">
      <h3>Réponses enregistrées</h3>
      {reponses.map((r) => <Ligne key={r.id} label={r.titre} valeur={<button className="qc-link" onClick={() => setVoir(r)}>Voir</button>} />)}
      {!reponses.length && <div className="qc-empty">Aucune réponse type.</div>}
      <button className="qc-btn qc-btn--sm" style={{ marginTop: 10 }} onClick={() => setAjout(true)}>+ Ajouter</button>
      {voir && (
        <Fenetre titre={voir.titre} onClose={() => setVoir(null)}>
          <div className="cm-texte">{voir.texte}</div>
          <div className="qc-foot">Modèle à adapter avant chaque envoi.</div>
          <div className="qc-actions"><button className="qc-btn qc-btn--danger" onClick={() => supprimer(voir)}>Supprimer</button></div>
        </Fenetre>
      )}
      {ajout && (
        <Fenetre titre="Nouvelle réponse type" onClose={() => setAjout(false)}>
          <div className="qc-form">
            <label className="qc-full">Titre<input value={valeurs.titre} onChange={(e) => setValeurs((v) => ({ ...v, titre: e.target.value }))} /></label>
            <label className="qc-full">Texte<textarea rows={5} value={valeurs.texte} onChange={(e) => setValeurs((v) => ({ ...v, texte: e.target.value }))} /></label>
          </div>
          <Message texte={message} />
          <div className="qc-actions"><button className="qc-btn qc-btn--primary" onClick={ajouter}>Enregistrer</button></div>
        </Fenetre>
      )}
    </div>
  );
}

export function CMCommunautePage() {
  const [donnees, setDonnees] = useState(null);
  const [reponses, setReponses] = useState([]);
  const [statut, setStatut] = useState("");
  const [recherche, setRecherche] = useState("");
  const [fiche, setFiche] = useState(null);
  const [nouvelle, setNouvelle] = useState(false);
  const charger = useCallback(() => {
    const params = new URLSearchParams();
    if (statut) params.set("statut", statut);
    if (recherche.trim()) params.set("q", recherche.trim());
    api.get(`cm/demandes/?${params}`).then((r) => setDonnees(r.data)).catch(() => setDonnees({ demandes: [], compteurs: {} }));
  }, [statut, recherche]);
  const chargerReponses = useCallback(() => { api.get("cm/reponses/").then((r) => setReponses(r.data.reponses || [])).catch(() => {}); }, []);
  useEffect(() => { const t = setTimeout(charger, 250); return () => clearTimeout(t); }, [charger]);
  useEffect(chargerReponses, [chargerReponses]);
  const c = donnees?.compteurs || {};
  return (
    <div className="qc">
      <Entete titre="Suivi de la communauté" sousTitre="Répondre, orienter les demandes et transmettre au support ce qui le concerne.">
        <button className="qc-btn qc-btn--primary" onClick={() => setNouvelle(true)}>+ Saisir une demande</button>
      </Entete>
      <div className="qc-kpis">
        <Kpi label="Nouvelles demandes" value={c.nouvelles ?? "—"} ton={TONS.navy} sub="À prendre en charge" />
        <Kpi label="En cours" value={c.en_cours ?? "—"} ton={TONS.amber} sub="Réponse à compléter" />
        <Kpi label="Transmises au support" value={c.transmises ?? "—"} ton={TONS.red} sub="Paiement, code, compte…" />
        <Kpi label="Clôturées cette semaine" value={c.cloturees_semaine ?? "—"} ton={TONS.green} sub="Depuis lundi" />
      </div>
      <div className="qc-card">
        <h3>File de suivi</h3>
        <div className="qc-toolbar">
          <input className="qc-search" placeholder="Rechercher une demande…" value={recherche} onChange={(e) => setRecherche(e.target.value)} />
          <select value={statut} onChange={(e) => setStatut(e.target.value)} aria-label="État">
            <option value="">Tous les états</option>
            {STATUTS_DEMANDE.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </div>
        {!donnees ? <Chargement /> : (
          <div className="qc-table-wrap">
            <table className="qc-table">
              <thead><tr><th>Demande</th><th>Source</th><th>Sujet</th><th>État</th><th>Action</th></tr></thead>
              <tbody>
                {donnees.demandes.map((d) => (
                  <tr key={d.id}>
                    <td>{d.reference} · {d.contact_nom || "—"}<small>{dateHeure(d.created_at)}</small></td>
                    <td>{d.source_label}</td>
                    <td>{d.sujet}</td>
                    <td><Chip ton={TON_DEMANDE[d.statut]}>{d.statut_label}</Chip></td>
                    <td><button className="qc-link" onClick={() => setFiche(d.id)}>Ouvrir</button></td>
                  </tr>
                ))}
                {!donnees.demandes.length && <tr><td colSpan={5} className="qc-empty">Aucune demande.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <div className="qc-cols qc-cols--half" style={{ marginTop: 16 }}>
        <ReponsesEnregistrees reponses={reponses} onChange={chargerReponses} />
        <div className="qc-card">
          <h3>Des droits précis</h3>
          <p className="qc-subtitle">Les demandes arrivent du formulaire « Nous contacter » du site et de l’application, ou sont saisies ici quand elles viennent des réseaux sociaux.</p>
          <div className="qc-note">Aucun accès aux conversations privées des élèves, aux paiements, aux codes ni à la comptabilité. Les problèmes de paiement ou de code sont transmis au support.</div>
        </div>
      </div>
      {fiche && <FicheDemande id={fiche} reponses={reponses} onClose={() => setFiche(null)} onChange={charger} />}
      {nouvelle && <NouvelleDemande onClose={() => setNouvelle(false)} onSaved={(d) => { setNouvelle(false); charger(); setFiche(d.id); }} />}
    </div>
  );
}

// ── Statistiques ────────────────────────────────────────────────────────────

const VIDE_STAT = { canal: "facebook", debut: "", fin: "", impressions: "", interactions: "", clics: "", source: "Saisie manuelle", note: "" };

function FormStatistique({ ligne, onClose, onSaved }) {
  const [valeurs, setValeurs] = useState(ligne ? Object.fromEntries(Object.keys(VIDE_STAT).map((k) => [k, ligne[k] ?? ""])) : VIDE_STAT);
  const [message, setMessage] = useState("");
  const maj = (cle) => (e) => setValeurs((v) => ({ ...v, [cle]: e.target.value }));
  const enregistrer = async () => {
    try {
      if (ligne) await api.put(`cm/statistiques/${ligne.id}/`, valeurs); else await api.post("cm/statistiques/", valeurs);
      onSaved();
    } catch (err) { setMessage(erreurApi(err)); }
  };
  return (
    <Fenetre titre={ligne ? "Modifier une saisie" : "Saisir des statistiques"} onClose={onClose}>
      <div className="qc-form">
        <label>Canal<select value={valeurs.canal} onChange={maj("canal")}>{CANAUX.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></label>
        <label>Source<input value={valeurs.source} onChange={maj("source")} /></label>
        <label>Début de la période<input type="date" value={valeurs.debut} onChange={maj("debut")} /></label>
        <label>Fin de la période<input type="date" value={valeurs.fin} onChange={maj("fin")} /></label>
        <label>Impressions (affichages)<input type="number" min="0" value={valeurs.impressions} onChange={maj("impressions")} /></label>
        <label>Interactions (réactions, commentaires, partages)<input type="number" min="0" value={valeurs.interactions} onChange={maj("interactions")} /></label>
        <label>Clics sur les liens (déclarés par le réseau)<input type="number" min="0" value={valeurs.clics} onChange={maj("clics")} /></label>
        <label>Note<input value={valeurs.note} onChange={maj("note")} maxLength={300} /></label>
      </div>
      <div className="qc-foot">Laissez vide une valeur inconnue : elle ne sera pas comptée comme zéro. Une période est rattachée au mois de son début.</div>
      <Message texte={message} />
      <div className="qc-actions"><button className="qc-btn qc-btn--primary" onClick={enregistrer}>Enregistrer</button></div>
    </Fenetre>
  );
}

function Rapport({ mois, onClose }) {
  const [donnees, setDonnees] = useState(null);
  const [difficultes, setDifficultes] = useState("");
  const [priorites, setPriorites] = useState("");
  useEffect(() => { api.get(`cm/rapport/?mois=${mois}`).then((r) => setDonnees(r.data)).catch(() => setDonnees(false)); }, [mois]);
  // Le rapport est produit en PDF par le serveur : le même fichier se télécharge ou s'imprime.
  const nom = `rapport-communication-${mois}.pdf`;
  const pdf = () => api.post(`cm/rapport/?mois=${mois}`, { difficultes, priorites }, { responseType: "blob" }).then((r) => r.data);
  const imprimer = () => pdf().then((data) => imprimerDocument({ data, filename: nom }))
    .catch(() => qlAlert("Impression impossible.", "error"));
  const telecharger = () => pdf()
    .then((data) => downloadFile({ url: `${api.defaults.baseURL}generated/${nom}`, filename: nom, mimeType: "application/pdf", data }))
    .then((r) => { if (!r?.cancelled) qlAlert("Rapport téléchargé.", "success"); })
    .catch(() => qlAlert("Téléchargement impossible.", "error"));
  return (
    <Fenetre titre={`Rapport d’activité · ${libelleMois(mois)}`} onClose={onClose}>
      {donnees === null ? <Chargement /> : donnees === false ? <div className="qc-empty">Rapport indisponible.</div> : <>
        <Ligne label="Publications" valeur={donnees.statistiques.publications} />
        <Ligne label="Impressions" valeur={nombre(donnees.statistiques.totaux.impressions)} />
        <Ligne label="Interactions" valeur={nombre(donnees.statistiques.totaux.interactions)} />
        <Ligne label="Demandes reçues / clôturées" valeur={`${donnees.demandes.recues} / ${donnees.demandes.cloturees}`} />
        <Ligne label="Contenus en préparation" valeur={`${donnees.contenus_en_cours.brouillon} brouillons · ${donnees.contenus_en_cours.a_valider} à valider · ${donnees.contenus_en_cours.valide} validés`} />
        <div className="qc-section-title">Contenus publiés</div>
        {donnees.publications.map((p, i) => <Ligne key={i} label={`${p.titre} (${p.canal})`} valeur={dateHeure(p.date)} />)}
        {!donnees.publications.length && <div className="qc-foot">Aucune publication enregistrée ce mois-ci.</div>}
        <div className="qc-form" style={{ marginTop: 12 }}>
          <label className="qc-full">Difficultés rencontrées<textarea rows={2} value={difficultes} onChange={(e) => setDifficultes(e.target.value)} /></label>
          <label className="qc-full">Priorités du mois suivant<textarea rows={2} value={priorites} onChange={(e) => setPriorites(e.target.value)} /></label>
        </div>
        <div className="qc-actions">
          <button className="qc-btn" onClick={imprimer}><i className="bi bi-printer"></i>Imprimer</button>
          <button className="qc-btn qc-btn--primary" onClick={telecharger}><i className="bi bi-download"></i>Télécharger (PDF)</button>
        </div>
      </>}
    </Fenetre>
  );
}

export function CMStatistiquesPage() {
  const [mois, setMois] = useState(jourCameroun().slice(0, 7));
  const [donnees, setDonnees] = useState(null);
  const [form, setForm] = useState(null);
  const [rapport, setRapport] = useState(false);
  const charger = useCallback(() => {
    setDonnees(null);
    api.get(`cm/statistiques/?mois=${mois}`).then((r) => setDonnees(r.data)).catch(() => setDonnees(false));
  }, [mois]);
  useEffect(charger, [charger]);
  const supprimer = (l) => qlConfirm("Supprimer cette saisie ?", async () => { await api.delete(`cm/statistiques/${l.id}/`); charger(); },
    { danger: true, okLabel: "Supprimer" });
  const max = Math.max(1, ...((donnees && donnees.series) || []).map((s) => s.impressions || 0));
  return (
    <div className="qc">
      <Entete titre="Statistiques & rapports" sousTitre="Comprendre quels contenus attirent et aident les élèves.">
        <div className="qc-toolbar" style={{ margin: 0 }}>
          <div className="qc-periode"><label>Mois</label><input type="month" value={mois} onChange={(e) => e.target.value && setMois(e.target.value)} /></div>
          <button className="qc-btn" onClick={() => setRapport(true)}><i className="bi bi-file-earmark-text"></i>Aperçu du rapport</button>
          <button className="qc-btn qc-btn--primary" onClick={() => setForm("nouveau")}>+ Saisir</button>
        </div>
      </Entete>
      {donnees === null ? <Chargement /> : donnees === false ? <div className="qc-card qc-empty">Chargement impossible.</div> : <>
        {donnees.mois_incomplet && <div className="cm-alerte">Mois en cours : les chiffres sont partiels.</div>}
        <div className="qc-kpis">
          <Kpi label="Publications du mois" value={donnees.publications} ton={TONS.navy} sub="Contenus enregistrés comme publiés" />
          <Kpi label="Impressions" value={nombre(donnees.totaux.impressions)} ton={TONS.navy} sub="Affichages, pas personnes uniques" />
          <Kpi label="Interactions" value={nombre(donnees.totaux.interactions)} ton={TONS.green} sub="Réactions, commentaires, partages" />
          <Kpi label="Taux d’engagement" value={pourcent(donnees.totaux.engagement)} ton={TONS.amber} sub="Interactions ÷ impressions" />
        </div>
        <div className="qc-cols">
          <div className="qc-card">
            <h3>Impressions par période saisie</h3>
            {donnees.series.length === 0 ? <div className="qc-empty">Aucune statistique saisie pour {libelleMois(mois)}.</div> : (
              <div className="cm-bars">
                {donnees.series.map((s) => (
                  <div key={`${s.debut}-${s.fin}`} className="cm-bar-col">
                    <span>{nombre(s.impressions)}</span>
                    <div className={`cm-bar${s.incomplete ? " cm-bar--partielle" : ""}`} style={{ height: `${((s.impressions || 0) / max) * 100}%` }} />
                    <small>{jourCourt(s.debut)}–{jourCourt(s.fin)}{s.incomplete ? "*" : ""}</small>
                  </div>
                ))}
              </div>
            )}
            {donnees.series.some((s) => s.incomplete) && <div className="qc-foot">* Période en cours : observation partielle.</div>}
          </div>
          <div className="qc-card">
            <h3>Du contenu à la plateforme</h3>
            <Ligne label="Clics sur les liens (déclarés par les réseaux)" valeur={nombre(donnees.totaux.clics)} />
            <Ligne label="Visites attribuées" valeur={donnees.suivi_attribution?.configure ? nombre(donnees.suivi_attribution.visites) : "Suivi non configuré"} />
            <Ligne label="Inscriptions attribuées" valeur={donnees.suivi_attribution?.configure ? nombre(donnees.suivi_attribution.inscriptions) : "Suivi non configuré"} />
            {donnees.suivi_attribution?.taux != null && <Ligne label="Taux de conversion" valeur={pourcent(donnees.suivi_attribution.taux)} />}
            {donnees.suivi_attribution?.par_campagne?.length > 0 && (
              <div className="qc-table-wrap" style={{ marginTop: 10 }}>
                <table className="qc-table">
                  <thead><tr><th>Campagne</th><th className="qc-num">Visites</th><th className="qc-num">Inscriptions</th><th className="qc-num">Conversion</th></tr></thead>
                  <tbody>
                    {donnees.suivi_attribution.par_campagne.map((c) => (
                      <tr key={c.titre}><td>{c.titre}</td><td className="qc-num">{nombre(c.visites)}</td><td className="qc-num">{nombre(c.inscriptions)}</td><td className="qc-num">{pourcent(c.taux)}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="qc-note">Seuls les visiteurs passés par un lien de suivi de campagne sont comptés (une visite par personne, par lien et par jour) ; une inscription est attribuée si le compte est créé dans les 30 jours. Ce sont des minimums mesurés, rien n’est estimé.</div>
          </div>
        </div>
        <div className="qc-card">
          <h3>Résultats par canal</h3>
          <div className="qc-table-wrap">
            <table className="qc-table">
              <thead><tr><th>Canal</th><th className="qc-num">Publications</th><th className="qc-num">Impressions</th><th className="qc-num">Interactions</th><th className="qc-num">Engagement</th><th>Saisies</th></tr></thead>
              <tbody>
                {donnees.par_canal.map((c) => (
                  <tr key={c.canal}><td>{c.canal_label}</td><td className="qc-num">{c.publications}</td><td className="qc-num">{nombre(c.impressions)}</td>
                    <td className="qc-num">{nombre(c.interactions)}</td><td className="qc-num">{pourcent(c.engagement)}</td><td>{c.saisies}</td></tr>
                ))}
                {!donnees.par_canal.length && <tr><td colSpan={6} className="qc-empty">Aucune donnée ce mois-ci.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
        <div className="qc-card" style={{ marginTop: 16 }}>
          <h3>Saisies du mois</h3>
          <div className="qc-table-wrap">
            <table className="qc-table">
              <thead><tr><th>Canal</th><th>Période</th><th className="qc-num">Impressions</th><th className="qc-num">Interactions</th><th>Source</th><th></th></tr></thead>
              <tbody>
                {donnees.lignes.map((l) => (
                  <tr key={l.id}>
                    <td>{l.canal_label}</td>
                    <td>{jourCourt(l.debut)} – {jourCourt(l.fin)}{l.incomplete && <small>En cours</small>}</td>
                    <td className="qc-num">{nombre(l.impressions)}</td><td className="qc-num">{nombre(l.interactions)}</td>
                    <td>{l.source}<small>{l.saisi_par} · {dateHeure(l.updated_at)}</small></td>
                    <td className="qc-num">
                      <button className="qc-btn qc-btn--sm" onClick={() => setForm(l)} aria-label="Modifier"><i className="bi bi-pencil"></i></button>{" "}
                      <button className="qc-btn qc-btn--sm qc-btn--danger" onClick={() => supprimer(l)} aria-label="Supprimer"><i className="bi bi-trash"></i></button>
                    </td>
                  </tr>
                ))}
                {!donnees.lignes.length && <tr><td colSpan={6} className="qc-empty">Rien n’a été saisi.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
        <div className="cm-bandeau">Statistiques saisies à la main depuis les réseaux : aucune synchronisation automatique dans cette version.</div>
      </>}
      {form && <FormStatistique ligne={form === "nouveau" ? null : form} onClose={() => setForm(null)} onSaved={() => { setForm(null); charger(); }} />}
      {rapport && <Rapport mois={mois} onClose={() => setRapport(false)} />}
    </div>
  );
}

// ── Ressources de marque ────────────────────────────────────────────────────

function Ressource({ ressource, peutApprouver, onChange }) {
  const [ouvert, setOuvert] = useState(false);
  const approuver = async () => {
    try { await api.patch(`cm/ressources/${ressource.id}/`, { approuvee: !ressource.approuvee }); onChange(); }
    catch (err) { qlAlert(erreurApi(err), "error"); }
  };
  const supprimer = () => qlConfirm(`Supprimer « ${ressource.titre} » ?`, async () => { await api.delete(`cm/ressources/${ressource.id}/`); onChange(); },
    { danger: true, okLabel: "Supprimer" });
  return (
    <div className="cm-ressource">
      {ressource.fichier_type === "image" && <img src={ressource.fichier_url} alt={ressource.titre} />}
      <div className="cm-chips"><Chip>{ressource.type_label}</Chip><Chip ton={ressource.approuvee ? "green" : "amber"}>{ressource.approuvee ? "Approuvée" : "À approuver"}</Chip></div>
      <strong>{ressource.titre}</strong>
      {ressource.texte && <div className="qc-foot" style={{ whiteSpace: "pre-wrap" }}>{ressource.texte}</div>}
      <div className="qc-actions" style={{ marginTop: 4 }}>
        {ressource.fichier_url && ressource.fichier_type !== "image" && (
          <button className="qc-btn qc-btn--sm" onClick={() => setOuvert(true)}>Ouvrir</button>
        )}
        {peutApprouver && <button className="qc-btn qc-btn--sm" onClick={approuver}>{ressource.approuvee ? "Retirer l’approbation" : "Approuver"}</button>}
        {ressource.peut_supprimer && <button className="qc-btn qc-btn--sm qc-btn--danger" onClick={supprimer}><i className="bi bi-trash"></i></button>}
      </div>
      {ouvert && (ressource.fichier_type === "video"
        ? <Fenetre titre={ressource.titre} onClose={() => setOuvert(false)}><video className="cm-media" src={ressource.fichier_url} controls /></Fenetre>
        : <DocumentViewer titre={ressource.titre} url={ressource.fichier_url} onClose={() => setOuvert(false)} />)}
    </div>
  );
}

export function CMRessourcesPage() {
  const [donnees, setDonnees] = useState(null);
  const [ajout, setAjout] = useState(false);
  const [valeurs, setValeurs] = useState({ titre: "", type: "logo", texte: "" });
  const [fichier, setFichier] = useState(null);
  const [message, setMessage] = useState("");
  const charger = useCallback(() => { api.get("cm/ressources/").then((r) => setDonnees(r.data)).catch(() => setDonnees(false)); }, []);
  useEffect(charger, [charger]);
  const enregistrer = async () => {
    try {
      await api.post("cm/ressources/", formulaire({ ...valeurs, ...(fichier ? { fichier } : {}) }), MULTIPART);
      setAjout(false); setValeurs({ titre: "", type: "logo", texte: "" }); setFichier(null); charger();
    } catch (err) { setMessage(erreurApi(err)); }
  };
  const groupes = donnees ? [["Ressources approuvées", donnees.ressources.filter((r) => r.approuvee)],
    ["En attente d’approbation", donnees.ressources.filter((r) => !r.approuvee)]] : [];
  return (
    <div className="qc">
      <Entete titre="Ressources de marque" sousTitre="Logos, couvertures et modèles approuvés, distincts des brouillons.">
        <button className="qc-btn qc-btn--primary" onClick={() => setAjout(true)}>+ Ajouter une ressource</button>
      </Entete>
      {donnees === null ? <Chargement /> : donnees === false ? <div className="qc-card qc-empty">Chargement impossible.</div> : groupes.map(([titre, liste]) => (
        <div key={titre} style={{ marginBottom: 18 }}>
          <div className="qc-section-title">{titre}</div>
          {liste.length ? (
            <div className="cm-ressources">{liste.map((r) => <Ressource key={r.id} ressource={r} peutApprouver={donnees.peut_approuver} onChange={charger} />)}</div>
          ) : <div className="qc-foot">Aucune.</div>}
        </div>
      ))}
      <div className="qc-note">Une ressource ajoutée par la Community Manager reste « à approuver » jusqu’à la décision du CEO.</div>
      {ajout && (
        <Fenetre titre="Ajouter une ressource" onClose={() => setAjout(false)}>
          <div className="qc-form">
            <label>Titre<input value={valeurs.titre} onChange={(e) => setValeurs((v) => ({ ...v, titre: e.target.value }))} /></label>
            <label>Type<select value={valeurs.type} onChange={(e) => setValeurs((v) => ({ ...v, type: e.target.value }))}>
              {Object.entries(donnees?.types || {}).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></label>
            <label className="qc-full">Fichier (image, vidéo ou PDF, 25 Mo max.)<input type="file" accept="image/*,video/mp4,video/webm,application/pdf" onChange={(e) => setFichier(e.target.files?.[0] || null)} /></label>
            <label className="qc-full">Texte validé (slogan, description…)<textarea rows={3} value={valeurs.texte} onChange={(e) => setValeurs((v) => ({ ...v, texte: e.target.value }))} /></label>
          </div>
          <Message texte={message} />
          <div className="qc-actions"><button className="qc-btn qc-btn--primary" onClick={enregistrer}>Enregistrer</button></div>
        </Fenetre>
      )}
    </div>
  );
}
